import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { listMemories, type MemoryState } from "../../api/threads/memoriesApi";
import { useLoad } from "../../shared/loading/useLoad";
import { CATALOGUE_STATES, deferredToItsThread } from "./memoryActions";
import MemoryRow from "./MemoryRow";
import SegmentedControl from "../../ui/navigation/SegmentedControl";
import LoadStatus from "../../ui/feedback/LoadStatus";

interface MemoryCatalogueProps {
  projectId: string;
  initialView?: MemoryState;
}

/**
 * The signed-in person's memory catalogue in a project (memory.md MEM-7), by state, with
 * what they may do with each. The page around it names it; cleanup is the library's "Clean
 * up memories" Workflow (MEM-6).
 */
export default function MemoryCatalogue({ projectId, initialView = "active" }: MemoryCatalogueProps) {
  const { t } = useTranslation();
  const [view, setView] = useState<MemoryState>(initialView);
  const loadMemories = useCallback((signal: AbortSignal) => listMemories(projectId, view, signal), [projectId, view]);
  const memories = useLoad(loadMemories);

  return (
    <section className="space-y-3" aria-label={t("memories.title")}>
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          label={t("memories.stateFilter")}
          options={CATALOGUE_STATES.map((state) => ({ key: state, label: t(`memories.state.${state}`) }))}
          selected={view}
          onSelect={setView}
          size="regular"
        />
      </div>
      <LoadStatus state={memories.state} loadingLabel={t("common.loading")} onRetry={memories.reload} />
      {memories.state.status === "loaded" && memories.state.value.length === 0 ? <p className="app-text-muted text-sm">{t("memories.empty")}</p> : null}
      {memories.state.status === "loaded" && memories.state.value.length > 0 ? (
        <ul className="space-y-3">
          {memories.state.value.map((memory) => (
            <MemoryRow key={memory.memoryId} projectId={projectId} memory={memory} onChanged={memories.refresh} underReview={deferredToItsThread(memory)} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
